import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CalendarDays,
  FileText,
  Pencil,
  Save,
  Send,
  Upload,
} from "lucide-react";
import { API, api, apiForm, formatApiError } from "@/lib/api";
import {
  JAWABAN_KEYS,
  KESIMPULAN_OPTIONS,
  daysBetween,
  formatTanggal,
  pickFileGuard,
} from "./shared";

const ALL_KEYS = [
  "perihal",
  "tgl_diterima",
  "tgl_dijawab",
  "kesimpulan",
  "pengelola",
  "dokumen_permohonan",
  "dokumen_jawaban",
];

export default function PengelolaView({ user }) {
  const [entries, setEntries] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [files, setFiles] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await api("/api/permohonan");
      setEntries(d.entries || []);
    } catch (e) {
      toast(formatApiError(e.message));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const draftOf = (e) => drafts[e.id] || {};
  const val = (e, k) => {
    const d = draftOf(e);
    return k in d ? d[k] : e[k] ?? "";
  };
  const fileOf = (e, k) => files[`${e.id}:${k}`] || null;

  // Isian yang boleh diubah pengelola: yang masih kosong; setelah submit,
  // sisi jawaban dapat diperbaiki melalui tombol Edit.
  const editable = (e, k) => {
    if (e.status === "final" && editingId !== e.id) return false;
    if (JAWABAN_KEYS.includes(k)) return editingId === e.id ? true : !e[k];
    return !e[k];
  };

  const merged = (e) => {
    const d = draftOf(e);
    const out = {};
    for (const k of ALL_KEYS) {
      if (k === "dokumen_permohonan" || k === "dokumen_jawaban") {
        out[k] = fileOf(e, k) ? {} : e[k];
      } else {
        out[k] = d[k] !== undefined ? d[k] : e[k];
      }
    }
    return out;
  };
  // Kelengkapan berdasar keadaan TERSIMPAN di server (bukan draf lokal)
  // agar tombol Submit hanya muncul setelah perubahan disimpan.
  const savedComplete = (e) => ALL_KEYS.every((k) => !!e[k]);
  const hasChanges = (e) =>
    Object.keys(drafts[e.id] || {}).length > 0 ||
    !!files[`${e.id}:dokumen_permohonan`] ||
    !!files[`${e.id}:dokumen_jawaban`];

  const setDraft = (e, k, v) =>
    setDrafts((d) => ({ ...d, [e.id]: { ...(d[e.id] || {}), [k]: v } }));

  const setFile = (e, k, f) =>
    setFiles((s) => ({ ...s, [`${e.id}:${k}`]: f }));

  const save = async (e) => {
    setBusyId(e.id);
    try {
      const fd = new FormData();
      const d = draftOf(e);
      if (d.perihal !== undefined) fd.append("perihal", d.perihal);
      if (d.tgl_diterima !== undefined) fd.append("tgl_diterima", d.tgl_diterima);
      if (d.tgl_dijawab !== undefined) fd.append("tgl_dijawab", d.tgl_dijawab);
      if (d.kesimpulan !== undefined) fd.append("kesimpulan", d.kesimpulan);
      const fp = fileOf(e, "dokumen_permohonan");
      if (fp) fd.append("dokumen_permohonan", fp);
      const fj = fileOf(e, "dokumen_jawaban");
      if (fj) fd.append("dokumen_jawaban", fj);
      await apiForm(`/api/permohonan/${e.id}`, fd);
      toast.success("Perubahan tersimpan");
      setDrafts((s) => ({ ...s, [e.id]: {} }));
      setFiles((s) => ({
        ...s,
        [`${e.id}:dokumen_permohonan`]: null,
        [`${e.id}:dokumen_jawaban`]: null,
      }));
      if (editingId === e.id) setEditingId(null);
      await load();
    } catch (err) {
      toast(formatApiError(err.message));
    } finally {
      setBusyId("");
    }
  };

  const submit = async (e) => {
    setBusyId(e.id);
    try {
      await save(e);
      await api(`/api/permohonan/${e.id}/submit`, { method: "POST" });
      toast.success("Permohonan dikirim — status FINAL");
      await load();
    } catch (err) {
      toast(formatApiError(err.message));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl font-semibold text-[#0B2440] md:text-4xl">
          Permohonan Informasi Publik
        </h1>
        <span className="rounded-full bg-[#1479BF]/10 px-3 py-1 text-xs font-bold text-[#1479BF]">
          Tahun 2026
        </span>
      </div>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-500">
        Daftar permohonan yang ditugaskan kepada {user?.label}. Lengkapi isian
        yang masih kosong, lalu tekan Submit bila seluruh isian sudah lengkap.
      </p>

      <div className="mt-8 space-y-4">
        {entries.length === 0 ? (
          <p
            data-testid="permohonan-pengelola-empty"
            className="rounded-2xl border border-dashed border-[#E3EBF3] bg-white px-6 py-10 text-center text-sm text-slate-400"
          >
            Belum ada permohonan yang ditugaskan kepada Anda.
          </p>
        ) : (
          entries.map((e, i) => {
            const m = merged(e);
            const h = daysBetween(m.tgl_diterima, m.tgl_dijawab);
            const editingFinal = e.status === "final" && editingId === e.id;
            const canEditNow = e.status !== "final" || editingFinal;
            return (
              <div
                key={e.id}
                data-testid={`permohonan-pengelola-${i + 1}`}
                className="rounded-2xl border border-[#E3EBF3] bg-white p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <p className="text-sm font-semibold leading-snug text-[#0B2440]">
                    {i + 1}. {e.perihal || "—"}
                  </p>
                  <div className="flex items-center gap-2">
                    <span
                      data-testid={`permohonan-pengelola-status-${i + 1}`}
                      className={`rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.08em] ${
                        e.status === "final"
                          ? "bg-emerald-50 text-emerald-600"
                          : "bg-amber-50 text-amber-600"
                      }`}
                    >
                      {e.status === "final" ? "FINAL" : "DRAF"}
                    </span>
                    {e.status === "final" && (
                      <button
                        data-testid={`permohonan-pengelola-edit-${i + 1}`}
                        onClick={() => setEditingId(editingFinal ? null : e.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-[#E3EBF3] px-2.5 py-1 text-[10px] font-bold text-slate-600 transition-colors duration-200 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                      >
                        <Pencil aria-hidden="true" className="h-3 w-3" />
                        {editingFinal ? "Tutup Edit" : "Edit"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  {/* Dokumen permohonan */}
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
                      Dokumen Permohonan
                    </span>
                    {e.dokumen_permohonan ? (
                      <a
                        href={`${API}/api/permohonan/file/${e.dokumen_permohonan.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-[#1479BF] underline"
                      >
                        <FileText aria-hidden="true" className="h-4 w-4" />
                        {e.dokumen_permohonan.name}
                      </a>
                    ) : editable(e, "dokumen_permohonan") ? (
                      <label className="mt-1.5 inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-lg border border-[#E3EBF3] px-3 py-2 text-xs font-semibold text-slate-600 transition-colors duration-200 hover:border-[#1479BF] hover:text-[#1479BF]">
                        <Upload aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">
                          {fileOf(e, "dokumen_permohonan")
                            ? fileOf(e, "dokumen_permohonan").name
                            : "Unggah dokumen permohonan (maks. 25MB)"}
                        </span>
                        <input
                          type="file"
                          hidden
                          data-testid={`permohonan-pengelola-file-permohonan-${i + 1}`}
                          accept=".pdf,.doc,.docx,.xls,.xlsx"
                          onChange={(ev) =>
                            pickFileGuard(
                              ev.target.files[0] || null,
                              (f) => setFile(e, "dokumen_permohonan", f)
                            )
                          }
                        />
                      </label>
                    ) : (
                      <p className="mt-1.5 text-xs text-slate-300">—</p>
                    )}
                  </div>

                  {/* Perihal */}
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
                      Perihal Dokumen
                    </span>
                    {editable(e, "perihal") ? (
                      <input
                        data-testid={`permohonan-pengelola-perihal-${i + 1}`}
                        value={val(e, "perihal")}
                        onChange={(ev) => setDraft(e, "perihal", ev.target.value)}
                        placeholder="Perihal permohonan"
                        className="mt-1.5 w-full rounded-lg border border-[#E3EBF3] px-3 py-2 text-sm outline-none transition focus:border-[#1479BF]"
                      />
                    ) : (
                      <p className="mt-1.5 text-sm text-[#0B2440]">{m.perihal || "—"}</p>
                    )}
                  </div>

                  {/* Tanggal diterima */}
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
                      Tanggal Diterima LLDIKTI VII
                    </span>
                    {editable(e, "tgl_diterima") ? (
                      <input
                        type="date"
                        data-testid={`permohonan-pengelola-tgl-diterima-${i + 1}`}
                        value={val(e, "tgl_diterima")}
                        onChange={(ev) => setDraft(e, "tgl_diterima", ev.target.value)}
                        className="mt-1.5 w-full rounded-lg border border-[#E3EBF3] px-3 py-2 text-sm outline-none transition focus:border-[#1479BF]"
                      />
                    ) : (
                      <p className="mt-1.5 text-sm text-[#0B2440]">
                        {formatTanggal(m.tgl_diterima)}
                      </p>
                    )}
                  </div>

                  {/* Tanggal dijawab */}
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
                      Tanggal Permohonan Dijawab
                    </span>
                    {editable(e, "tgl_dijawab") ? (
                      <input
                        type="date"
                        data-testid={`permohonan-pengelola-tgl-dijawab-${i + 1}`}
                        value={val(e, "tgl_dijawab")}
                        onChange={(ev) => setDraft(e, "tgl_dijawab", ev.target.value)}
                        className="mt-1.5 w-full rounded-lg border border-[#E3EBF3] px-3 py-2 text-sm outline-none transition focus:border-[#1479BF]"
                      />
                    ) : (
                      <p className="mt-1.5 text-sm text-[#0B2440]">
                        {formatTanggal(m.tgl_dijawab)}
                      </p>
                    )}
                  </div>

                  {/* Dokumen jawaban */}
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
                      Dokumen Jawaban
                    </span>
                    {e.dokumen_jawaban && !fileOf(e, "dokumen_jawaban") ? (
                      <a
                        href={`${API}/api/permohonan/file/${e.dokumen_jawaban.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-[#1479BF] underline"
                      >
                        <FileText aria-hidden="true" className="h-4 w-4" />
                        {e.dokumen_jawaban.name}
                      </a>
                    ) : editable(e, "dokumen_jawaban") ? (
                      <label className="mt-1.5 inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-lg border border-[#E3EBF3] px-3 py-2 text-xs font-semibold text-slate-600 transition-colors duration-200 hover:border-[#1479BF] hover:text-[#1479BF]">
                        <Upload aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">
                          {fileOf(e, "dokumen_jawaban")
                            ? fileOf(e, "dokumen_jawaban").name
                            : "Unggah dokumen jawaban (maks. 25MB)"}
                        </span>
                        <input
                          type="file"
                          hidden
                          data-testid={`permohonan-pengelola-file-jawaban-${i + 1}`}
                          accept=".pdf,.doc,.docx,.xls,.xlsx"
                          onChange={(ev) =>
                            pickFileGuard(
                              ev.target.files[0] || null,
                              (f) => setFile(e, "dokumen_jawaban", f)
                            )
                          }
                        />
                      </label>
                    ) : (
                      <p className="mt-1.5 text-xs text-slate-300">—</p>
                    )}
                  </div>

                  {/* Kesimpulan */}
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
                      Kesimpulan Jawaban
                    </span>
                    {editable(e, "kesimpulan") ? (
                      <select
                        data-testid={`permohonan-pengelola-kesimpulan-${i + 1}`}
                        value={val(e, "kesimpulan")}
                        onChange={(ev) => setDraft(e, "kesimpulan", ev.target.value)}
                        className="mt-1.5 w-full rounded-lg border border-[#E3EBF3] bg-white px-3 py-2 text-sm outline-none transition focus:border-[#1479BF]"
                      >
                        <option value="">— Pilih kesimpulan —</option>
                        {KESIMPULAN_OPTIONS.map((k) => (
                          <option key={k} value={k}>
                            {k}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p
                        className="mt-1.5 max-w-[420px] truncate text-sm text-[#0B2440]"
                        title={m.kesimpulan || ""}
                      >
                        {m.kesimpulan || "—"}
                      </p>
                    )}
                  </div>
                </div>

                {h != null && (
                  <p
                    data-testid={`permohonan-pengelola-hari-${i + 1}`}
                    className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                      h >= 0 ? "bg-[#EFF6FC] text-[#1479BF]" : "bg-red-50 text-red-500"
                    }`}
                  >
                    <CalendarDays aria-hidden="true" className="h-3.5 w-3.5" />
                    {h >= 0
                      ? `Terjawab dalam ${h} hari`
                      : "Tanggal dijawab mendahului tanggal diterima"}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap gap-2 border-t border-[#E3EBF3] pt-4">
                  {canEditNow && hasChanges(e) && (
                    <button
                      data-testid={`permohonan-pengelola-save-${i + 1}`}
                      onClick={() => save(e)}
                      disabled={busyId === e.id}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#1479BF] px-4 py-1.5 text-[11px] font-bold text-[#1479BF] transition-colors duration-200 hover:bg-[#EFF6FC] disabled:opacity-40"
                    >
                      <Save aria-hidden="true" className="h-3.5 w-3.5" />
                      Simpan Perubahan
                    </button>
                  )}
                  {e.status !== "final" && savedComplete(e) && (
                    <button
                      data-testid={`permohonan-pengelola-submit-${i + 1}`}
                      onClick={() => submit(e)}
                      disabled={busyId === e.id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#1479BF] px-4 py-1.5 text-[11px] font-bold text-white transition-colors duration-200 hover:bg-[#0B2440] disabled:opacity-40"
                    >
                      <Send aria-hidden="true" className="h-3.5 w-3.5" />
                      Submit
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}